import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, expand, finalize, Observable, tap } from 'rxjs';
import { TSP } from '../../core/models';
import { AdminService } from '../../core/services/admin/admin.service';
import { CityService } from '../../core/services/city/city.service';

export type VacancyBackfillKind = 'competences' | 'sourcingIntent';

export interface VacancyBackfillRun {
  running: boolean;
  /** Vacancies filled by the passes of the last run. */
  filled: number;
  passes: number;
}

/** vacancy-service's default batch, and its cap. */
export const DEFAULT_BACKFILL_LIMIT = 25;
export const MAX_BACKFILL_LIMIT = 200;

/**
 * A run "until done" stops here even if passes keep filling something: every
 * vacancy in a pass costs a model call, and 50 × 200 is well past today's
 * backlog. Hitting it means something is off — look before running again.
 */
export const MAX_BACKFILL_PASSES = 50;

/**
 * One-off maintenance of vacancies saved before vacancy-service learned to
 * extract their competences and sourcing intent, plus the robota.ua city
 * dictionary behind the external search links.
 *
 * The page exists because vacancy-service and dictionary-service have no route
 * out of the cluster; the gateway's `admin/` routes are the only way in.
 */
@Component({
  selector: 'app-vacancy-maintenance',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatFormField,
    MatInput,
    MatLabel,
  ],
  templateUrl: './vacancy-maintenance.component.html',
  styleUrls: ['./vacancy-maintenance.component.scss'],
})
export class VacancyMaintenanceComponent {
  private readonly adminService = inject(AdminService);
  private readonly cityService = inject(CityService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  public readonly maxLimit = MAX_BACKFILL_LIMIT;
  public limit = DEFAULT_BACKFILL_LIMIT;

  public readonly runs: Record<VacancyBackfillKind, VacancyBackfillRun> = {
    competences: { running: false, filled: 0, passes: 0 },
    sourcingIntent: { running: false, filled: 0, passes: 0 },
  };

  public isSyncingRobotaUa = false;

  /** One pass of `limit` vacancies. */
  public runOnce(kind: VacancyBackfillKind): void {
    this.run(kind, false);
  }

  /** Passes one after another until one fills nothing, or `MAX_BACKFILL_PASSES` is reached. */
  public runUntilDone(kind: VacancyBackfillKind): void {
    this.run(kind, true);
  }

  public syncRobotaUa(): void {
    this.isSyncingRobotaUa = true;

    this.cityService
      .syncRobotaUaCities()
      .pipe(
        finalize(() => (this.isSyncingRobotaUa = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ result }) =>
          this.snackBar.open(`Довідник robota.ua оновлено: ${result} міст`, 'OK', { duration: 5000 }),
        error: () => this.snackBar.open('Не вдалося оновити довідник robota.ua.', 'OK', { duration: 4000 }),
      });
  }

  private run(kind: VacancyBackfillKind, untilDone: boolean): void {
    const run = this.runs[kind];
    const limit = this.limit;
    const pass = (): Observable<TSP.Helpers.NumericResponse> =>
      this.request(kind, limit).pipe(
        tap(({ result }) => {
          run.filled += result;
          run.passes += 1;
        }),
      );

    run.running = true;
    run.filled = 0;
    run.passes = 0;

    pass()
      .pipe(
        expand(({ result }) => (untilDone && result > 0 && run.passes < MAX_BACKFILL_PASSES ? pass() : EMPTY)),
        finalize(() => (run.running = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        complete: () =>
          this.snackBar.open(`Заповнено вакансій: ${run.filled} за ${run.passes} прох.`, 'OK', { duration: 5000 }),
        error: () => this.snackBar.open('Бекфіл перервався з помилкою.', 'OK', { duration: 4000 }),
      });
  }

  private request(kind: VacancyBackfillKind, limit: number): Observable<TSP.Helpers.NumericResponse> {
    return kind === 'competences'
      ? this.adminService.backfillVacancyCompetences(limit)
      : this.adminService.backfillVacancySourcingIntent(limit);
  }
}
